import QRCodeLabel from './QRCodeLabel';
import PhotoGallery from './PhotoGallery';

/**
 * A field collection as Lab Mode shows it after a scan: the label (code + QR),
 * what it is, the photographs taken in the field, and where and in what it
 * grew.
 *
 * The data comes from the collection's own routes (GET /collections/{id},
 * /photos, /vegetation), which decide who may see what -- a student gets a 404
 * on someone else's specimen, a collector a reduced view. `/scan` itself only
 * says what is printed on the label (routes/lookup.py, by design), so a
 * restricted record shows the label and says the rest is not available.
 */
export default function FieldRecordPanel({ code, title, field, t }) {
  const detail = field?.detail;
  const vegetation = [...(field?.vegetation || [])]
    .sort((a, b) => Number(Boolean(b.is_dominant)) - Number(Boolean(a.is_dominant)));
  const hasGps = detail?.gps_lat != null && detail?.gps_lng != null;
  const rows = detail ? [
    ['field.collected', detail.collection_datetime ? String(detail.collection_datetime).slice(0, 16).replace('T', ' ') : null],
    ['field.habitat', detail.habitat_biome],
    ['field.microenvironment', detail.microenvironment],
    ['field.host', detail.host_species],
    ['field.substrate', detail.substrate ? String(detail.substrate).replace(/_/g, ' ') : null],
    ['field.place', [detail.locality_text, detail.region, detail.country_code].filter(Boolean).join(' · ') || null],
    ['field.altitude', detail.altitude_m != null ? `${detail.altitude_m} m` : null],
    ['field.stage', detail.phenological_stage],
    ['field.growth', detail.growth_pattern],
  ].filter(([, v]) => v) : [];

  return (
    <section style={styles.card} data-testid="field-record">
      <div style={styles.head}>
        <QRCodeLabel value={code} size={180} />
        <div style={{ minWidth: 0 }}>
          <div style={styles.kicker}>{t('field.label')}</div>
          <div style={styles.code} data-testid="field-record-code">{code}</div>
          <div style={styles.name} data-testid="field-record-name">
            <em>{detail?.current_taxon_name || title || t('field.unidentified')}</em>
            {detail?.current_rank ? <span style={styles.rank}> ({detail.current_rank})</span> : null}
          </div>
        </div>
      </div>

      {field?.loading && <p style={styles.muted}>{t('field.loading')}</p>}
      {field?.restricted && <p style={styles.muted} data-testid="field-record-restricted">{t('field.restricted')}</p>}

      {detail && (
        <>
          <h3 style={styles.h3}>{t('field.photos')}</h3>
          {(field.photos || []).length > 0
            ? <PhotoGallery photos={field.photos} thumbWidth={150} thumbHeight={110} testId="field-record-photos" />
            : <p style={styles.muted}>{t('field.noPhotos')}</p>}

          <h3 style={styles.h3}>{t('field.where')}</h3>
          <dl style={styles.dl}>
            {rows.map(([key, value]) => (
              <div key={key} style={styles.row}>
                <dt style={styles.dt}>{t(key)}</dt>
                <dd style={styles.dd}>{value}</dd>
              </div>
            ))}
            {hasGps && (
              <div style={styles.row}>
                <dt style={styles.dt}>{t('field.gps')}</dt>
                <dd style={styles.dd} data-testid="field-record-gps">
                  <span dir="ltr">{Number(detail.gps_lat).toFixed(5)}, {Number(detail.gps_lng).toFixed(5)}</span>
                  {' '}
                  <a href={`https://www.openstreetmap.org/?mlat=${detail.gps_lat}&mlon=${detail.gps_lng}#map=16/${detail.gps_lat}/${detail.gps_lng}`}
                     target="_blank" rel="noopener noreferrer" title={t('field.mapTip')}>{t('field.map')}</a>
                </dd>
              </div>
            )}
            {vegetation.length > 0 && (
              <div style={styles.row}>
                <dt style={styles.dt}>{t('field.vegetation')}</dt>
                <dd style={styles.dd} data-testid="field-record-vegetation">
                  {vegetation.map((v) => (v.hebrew_name ? `${v.species} (${v.hebrew_name})` : v.species)
                    + (v.is_dominant ? ` · ${t('field.dominant')}` : '')).join('; ')}
                </dd>
              </div>
            )}
          </dl>

          {detail.general_notes && (
            <>
              <h3 style={styles.h3}>{t('field.observations')}</h3>
              <p style={styles.notes} data-testid="field-record-notes">{detail.general_notes}</p>
            </>
          )}
        </>
      )}
    </section>
  );
}

const styles = {
  card: {
    gridColumn: '1 / -1', background: 'var(--c-surface)', border: '1px solid var(--c-border)',
    borderRadius: 12, padding: 20,
  },
  head: { display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 },
  kicker: { fontSize: 12, textTransform: 'uppercase', color: 'var(--c-text-muted-2-fg)' },
  code: {
    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 22, fontWeight: 700,
    wordBreak: 'break-all',
  },
  name: { fontSize: 18, marginTop: 4 },
  rank: { fontSize: 14, color: 'var(--c-text-muted-2-fg)' },
  h3: { fontSize: 15, margin: '16px 0 8px' },
  dl: { margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 20px' },
  row: { display: 'flex', flexDirection: 'column' },
  dt: { fontSize: 12, color: 'var(--c-text-muted-2-fg)' },
  dd: { margin: 0, fontSize: 15 },
  notes: { margin: 0, whiteSpace: 'pre-wrap' },
  muted: { color: 'var(--c-text-muted-2-fg)', fontSize: 14 },
};
